import React, { useMemo, useState, useEffect } from 'react';
import { useIntl, useDispatch } from 'umi';
import { Card } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import PublicTrueDataCollection from '@intelligence/pages/TopCharts/components/PublicTrueDataCollection';
import USRevenueUnits from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPIOverview/components/USRevenueUnits';
import DataBrainCalibrationContent from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPIOverview/components/DataBrainCalibrationContent';
import SteamDataContent from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPIOverview/components/SteamDataContent';
import EngagementContent from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPIOverview/components/EngagementContent';
import StreamingContent from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPIOverview/components/StreamingContent';
import useKPIOverview from './hooks/useKPIOverview';
// import PublicDataCollection from './components/PublicDataCollection';
import MetricCardHiddenTips from './components/MetricCardHiddenTips';
import { buriedPointGame } from '@home/utils/buriedPoint';
import { dbiPageLogMes } from '@intelligence/utils/buriedPointCfg';
import { DBI_TOPCHARTS } from '@intelligence/models/topcharts';
import { LANGUAGE_CN } from '@home/_config/config';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';

import './index.scss';

interface IKPIOverviewProps extends IBaseProps {
  gameId: string;
}

const KPIOverview: React.FC<IKPIOverviewProps> = (props) => {
  const { gameId, entityType } = props;

  const { locale } = useIntl();
  const isCN = locale === LANGUAGE_CN;

  const dispatch = useDispatch();

  // 判断是否是 PC & Console Games 页面
  const isPCConsoleGamesPage = entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE;
  const {
    combineBasicInfoLoading,
    // derive whether to show Databrain-calibrated content or US Revenue & Units
    hasDatabrainCalibratedData,
    // DataBrain Calibration
    getDatabrainRevenueUnits,
    databrainRevenueUnitsLoading,
    databrainRevenueUnitsDatas,
    // Engagement Content
    getEngagementData,
    engagementLoading,
    engagementDataList,
    engagementRankLoading,
    // Steam Data Content
    getSteamCardData,
    steamLoading,
    steamDataList,
    steamHourlyCcuRawItem,
    steamRankLoading,
    kpiPlayerData,
    kpiPlaytimeData,
    combineBasicInfo,
    // Streaming Content
    getStreamingCardData,
    streamingLoading,
    streamingDataList,
    streamingRankLoading,
    // US Revenue Units
    getUSRevenueUnits,
    usRevenueUnitsLoading,
    usRevenueUnitsDatas,
  } = useKPIOverview({ gameId, isCN });

  /**
   * Steam Data模块是否存在无数据的指标
   */
  const [steamDataHasEmptyMetric, setSteamDataHasEmptyMetric] = useState(false);

  /**
   * Steam Data模块是否存在无数据的折线图
   */
  const [steamDataHasEmptyLineData, setSteamDataHasEmptyLineData] = useState(false);

  /**
   * Other Vendor模块是否无数据的指标
   */
  const [vendorDataHasEmptyMetric, setVendorDataHasEmptyMetric] = useState(false);

  /**
   * Streaming模块是否存在无数据的指标
   */
  const [streamingDataHasEmptyMetric, setStreamingDataHasEmptyMetric] = useState(false);

  /**
   * 是否展示页面底部的无数据提示，包含切换是否展示无数据卡片
   */
  const showNoDataExplanation = useMemo<boolean>(() => {
    return (
      steamDataHasEmptyMetric || steamDataHasEmptyLineData || vendorDataHasEmptyMetric || streamingDataHasEmptyMetric
    );
  }, [steamDataHasEmptyMetric, steamDataHasEmptyLineData, vendorDataHasEmptyMetric, streamingDataHasEmptyMetric]);

  /**
   * 是否展示无数据的指标卡片
   */
  const [showNoDataMetricCard, setShowNoDataMetricCard] = useState(false);

  const handlePageLog = (gameId: string) => {
    const pageLogMes = dbiPageLogMes.pcConsoleGameDetailsKPIOverview;
    if (gameId) {
      buriedPointGame(pageLogMes, gameId);
    }
    // 每次打点的时候，记录当前打点信息用于详情页头部的feedback参数
    recordPageMesForFeedback(pageLogMes);
  };

  const recordPageMesForFeedback = (logMes: any) => {
    dispatch({
      type: `${DBI_TOPCHARTS}/setActivedPageLogMes`,
      payload: logMes,
    });
  };

  useEffect(() => {
    handlePageLog(gameId);
  }, []);

  /**
   * 优先展示Databrain校准数据，其次 US Revenue & Units
   */
  const dynamicMetricsCard = (() => {
    if (combineBasicInfoLoading) {
      return (
        <Card>
          <Card.Body>
            <Loading style={{ height: 178 }} />
          </Card.Body>
        </Card>
      );
    }

    // Databrain校准数据
    if (hasDatabrainCalibratedData) {
      return (
        <DataBrainCalibrationContent
          {...props}
          gameName={combineBasicInfo?.basic_info?.name || ''} // Using gameId as gameName for now, should be replaced with actual game name
          getDatabrainRevenueUnits={getDatabrainRevenueUnits}
          databrainRevenueUnitsLoading={databrainRevenueUnitsLoading}
          databrainRevenueUnitsDatas={databrainRevenueUnitsDatas}
        />
      );
    }

    // US Revenue & Units
    return (
      <USRevenueUnits
        {...props}
        getUSRevenueUnits={getUSRevenueUnits}
        usRevenueUnitsLoading={usRevenueUnitsLoading}
        usRevenueUnitsDatas={usRevenueUnitsDatas}
      />
    );
  })();

  return (
    <div className="mobile-games-overly">
      {/* Public True Data区域 */}
      <PublicTrueDataCollection
        {...props}
        gameId={gameId}
        publicTrueDataReportBtnLogKey="gameDetailsKPIOverviewTrueAllDataReport"
      />
      {dynamicMetricsCard}
      {/* Steam Data卡片区域 */}
      <SteamDataContent
        {...props}
        showNoDataMetricCard={showNoDataMetricCard}
        setSteamDataHasEmptyMetric={setSteamDataHasEmptyMetric}
        setSteamDataHasEmptyLineData={setSteamDataHasEmptyLineData}
        getSteamCardData={getSteamCardData}
        steamLoading={steamLoading}
        steamDataList={steamDataList}
        steamHourlyCcuRawItem={steamHourlyCcuRawItem}
        steamRankLoading={steamRankLoading}
        kpiPlayerData={kpiPlayerData}
        kpiPlaytimeData={kpiPlaytimeData}
        isGamalyticSource={isPCConsoleGamesPage}
      />
      {/* Engagement卡片区域 */}
      <EngagementContent
        {...props}
        showNoDataMetricCard={showNoDataMetricCard}
        setVendorDataHasEmptyMetric={setVendorDataHasEmptyMetric}
        getEngagementData={getEngagementData}
        engagementLoading={engagementLoading}
        engagementDataList={engagementDataList}
        engagementRankLoading={engagementRankLoading}
      />
      {/* Streaming卡片区域 */}
      <StreamingContent
        {...props}
        showNoDataMetricCard={showNoDataMetricCard}
        setStreamingDataHasEmptyMetric={setStreamingDataHasEmptyMetric}
        getStreamingCardData={getStreamingCardData}
        streamingLoading={streamingLoading}
        streamingDataList={streamingDataList}
        streamingRankLoading={streamingRankLoading}
      />
      {/* Public Data Collection卡片区域 */}
      {/* 0115热更，产品要求隐藏掉PublicDataCollection */}
      {/* <PublicDataCollection {...props}  /> */}
      {showNoDataExplanation && (
        <MetricCardHiddenTips
          show={showNoDataMetricCard}
          onShow={() => {
            setShowNoDataMetricCard(true);
          }}
          onHide={() => {
            setShowNoDataMetricCard(false);
          }}
        />
      )}
    </div>
  );
};

export default KPIOverview;
